import { ApolloClient, InMemoryCache } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client";
import { createApolloProvider } from "@vue/apollo-option";
import fragmentTypesData from "./fragmentTypes.json";
import EventManager from "./plugins/GlobalEvents/EventManager";

const AUTH_TOKEN = "auth-token";

const httpEndpoint =
  process.env.VUE_APP_GRAPHQL_HTTP || "http://localhost:8000/graphql";

const possibleTypes = {};

fragmentTypesData.__schema.types.forEach((type) => {
  if (type.kind === "UNION" || type.kind === "INTERFACE") {
    possibleTypes[type.name] = type.possibleTypes.map((t) => t.name);
  }
});

const cache = new InMemoryCache({
  possibleTypes,
});

const uploadLink = createUploadLink({
  uri: httpEndpoint,
});

const authLink = setContext(async (_, { headers }) => {
  const token =
    typeof window !== "undefined" ? localStorage.getItem(AUTH_TOKEN) : null;
  const authorizationHeader = token ? { Authorization: `Bearer ${token}` } : {};
  return {
    headers: {
      ...headers,
      ...authorizationHeader,
    },
  };
});

const link = authLink.concat(uploadLink);

export function createProvider(options = {}) {
  const apolloClient = new ApolloClient({
    link,
    cache,
    connectToDevTools: process.env.NODE_ENV !== "production",
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
      },
    },
    ...options.apollo,
  });

  const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {},
    },
    errorHandler(error) {
      let evManager = EventManager.get();
      if (error.graphQLErrors && error.graphQLErrors.length) {
        for (let e of error.graphQLErrors) {
          if (e.extensions) {
            if (e.extensions.category && e.extensions.category === "http") {
              switch (e.extensions.code) {
                case 404:
                  evManager.trigger("not-found-error", e);
                  break;
                case 401:
                  evManager.trigger("unauthorized-error", e);
                  break;
                case 403:
                  evManager.trigger("forbidden-error", e);
                  break;
                default:
                  evManager.trigger("other-http-error", e);
                  break;
              }
            }
            return;
          }
        }
      }
      console.log(
        "%cError",
        "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;",
        error.message
      );
      evManager.trigger("unknown-gql-error", error);
    },
  });

  return apolloProvider;
}

function getClient(apolloOrClient) {
  if (apolloOrClient && apolloOrClient.resetStore) {
    return apolloOrClient;
  }
  if (apolloOrClient && apolloOrClient.provider) {
    return apolloOrClient.provider.defaultClient;
  }
  if (apolloOrClient && apolloOrClient.getClient) {
    return apolloOrClient.getClient();
  }
  return apolloOrClient;
}

export async function onLogin(apolloOrClient, token) {
  const apolloClient = getClient(apolloOrClient);
  if (typeof localStorage !== "undefined" && token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }
  try {
    await apolloClient.resetStore();
  } catch (e) {
    console.log("%cError on cache reset (login)", "color: orange;", e.message);
  }
  let evManager = EventManager.get();
  evManager.trigger("login");
}

export async function onLogout(apolloOrClient) {
  const apolloClient = getClient(apolloOrClient);
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem("perm:contextPermissions");
    localStorage.removeItem("perm:directPermissions");
    localStorage.removeItem("sidebar");
    localStorage.removeItem("user_id");
  }
  try {
    await apolloClient.resetStore();
  } catch (e) {
    console.log("%cError on cache reset (logout)", "color: orange;", e.message);
  }
  let evManager = EventManager.get();
  evManager.trigger("logout");
}
